'use client';
import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@/lib/utils';

/* shadcn-style popover (Radix). Portaled → never clipped by card/overflow.
 *
 * pointer-events-auto on every content: a parent Radix modal (Dialog/Sheet) parks
 * `pointer-events: none` on the body, and this portaled layer is a body sibling, not a modal
 * descendant. Radix would re-enable it through the shared layer stack, but the dialog and the
 * popover resolve different `@radix-ui/react-dismissable-layer` copies, so the stack never sees
 * both: every click inside a popover opened from a modal fell through to the modal below
 * (the patient picker in the invite dialog selected nothing). Outside a modal it is a no-op. */

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;

export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 6, onFocusOutside, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      // Same split layer stack, focus side: a parent modal's focus trap pulls focus back from
      // the portaled popover, and Safari (iPad) then read that as focus leaving and closed it
      // the moment it opened (the reschedule date, 2026-11-03). Tapping outside and Escape
      // still close it.
      onFocusOutside={(event) => {
        onFocusOutside?.(event);
        event.preventDefault();
      }}
      className={cn(
        'medlux-drop pointer-events-auto z-50 w-72 rounded-lg border border-border bg-popover p-4 text-popover-foreground shadow-lg outline-none',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = 'PopoverContent';
